/**
 * Flow-derivation helpers for the AI Service detail page's Agents tab (task 796).
 *
 * The Agents tab reads one document — the service's collaboration-config
 * `definition` — and every structural question the tab asks is answered from it:
 * which agents the flow references, in what order, at which nodes, and what sits
 * either side of those nodes. Those derivations live here, as pure functions
 * over the stored JSON, so the panels stay presentational and the parsing is
 * tested once rather than five times.
 *
 * The definition arrives typed as `Record<string, unknown>` (see
 * `CollaborationConfig.definition`), so everything here narrows defensively
 * rather than trusting the shape — a malformed or partially-written definition
 * must render an empty tab, never throw inside a panel.
 *
 * **Top-level nodes only.** Branch nodes can nest their own `nodes`/`edges`, but
 * this reads the definition's top-level node list exactly as the tab has always
 * done (task 380). Nested agents would need their own edge context to place on a
 * flow strip, which is a larger question than this tab; keeping the same reach
 * means the rail's membership is unchanged by this redesign.
 */
import type {
  AgentNode,
  CollaborationEdge,
  CollaborationNode,
} from '@/types/definition';

/**
 * One agent as the flow references it: the agent's id plus EVERY node it
 * occupies, in declaration order.
 *
 * Report open question 2 resolved: the rail carries one entry per agent (not per
 * node appearance), and the Role-in-this-flow panel is where each appearance and
 * its node-level settings are listed separately.
 */
export interface AgentFlowEntry {
  agentId: string;
  /** Every node in the flow that runs this agent, in declaration order. */
  nodes: AgentNode[];
}

/** The definition's top-level nodes, narrowed to objects with an `id`. */
export function nodesFromDefinition(
  definition: Record<string, unknown> | null | undefined
): CollaborationNode[] {
  const raw = definition?.nodes;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (node): node is CollaborationNode =>
      !!node && typeof node === 'object' && typeof (node as CollaborationNode).id === 'string'
  );
}

/** The definition's top-level edges, narrowed to those with both endpoints. */
export function edgesFromDefinition(
  definition: Record<string, unknown> | null | undefined
): CollaborationEdge[] {
  const raw = definition?.edges;
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (edge): edge is CollaborationEdge =>
      !!edge &&
      typeof edge === 'object' &&
      typeof (edge as CollaborationEdge).from === 'string' &&
      typeof (edge as CollaborationEdge).to === 'string'
  );
}

/** Agent nodes only — those carrying a non-empty `agentId`. */
export function agentNodesFromDefinition(
  definition: Record<string, unknown> | null | undefined
): AgentNode[] {
  return nodesFromDefinition(definition).filter(
    (node): node is AgentNode =>
      node.type === 'agent' && typeof (node as AgentNode).agentId === 'string' && !!(node as AgentNode).agentId
  );
}

/**
 * The rail's contents: one entry per distinct agent, ordered by where the agent
 * FIRST appears in the node list, each carrying all of its node appearances.
 */
export function agentEntriesFromDefinition(
  definition: Record<string, unknown> | null | undefined
): AgentFlowEntry[] {
  const entries: AgentFlowEntry[] = [];
  const byId = new Map<string, AgentFlowEntry>();

  for (const node of agentNodesFromDefinition(definition)) {
    const existing = byId.get(node.agentId);
    if (existing) {
      existing.nodes.push(node);
      continue;
    }
    const entry: AgentFlowEntry = { agentId: node.agentId, nodes: [node] };
    byId.set(node.agentId, entry);
    entries.push(entry);
  }

  return entries;
}

/**
 * Unique agent ids referenced by the definition's agent nodes, in node order.
 *
 * Kept as a named export because it is the tab's oldest published helper
 * (task 380) and is asserted on directly by the tab's tests.
 */
export function agentIdsFromDefinition(
  definition: Record<string, unknown> | null | undefined
): string[] {
  return agentEntriesFromDefinition(definition).map((entry) => entry.agentId);
}

/** What to call a node on the flow strip: its own name, then alias, then id. */
export function nodeLabel(node: CollaborationNode): string {
  const named = node as CollaborationNode & { alias?: string };
  return named.name || named.alias || node.id;
}

/** Nodes immediately before and after `nodeId`, resolved through the edge list. */
export function neighborsOfNode(
  definition: Record<string, unknown> | null | undefined,
  nodeId: string
): { upstream: CollaborationNode[]; downstream: CollaborationNode[] } {
  const nodes = nodesFromDefinition(definition);
  const edges = edgesFromDefinition(definition);
  const find = (id: string) => nodes.find((node) => node.id === id);

  const resolve = (ids: string[]): CollaborationNode[] => {
    const seen = new Set<string>();
    const out: CollaborationNode[] = [];
    for (const id of ids) {
      if (seen.has(id)) continue;
      seen.add(id);
      const node = find(id);
      // An edge can name a node the definition never declared (a half-finished
      // config). Show the bare id rather than dropping the connection silently.
      out.push(node ?? ({ id, type: 'function', function: '' } as CollaborationNode));
    }
    return out;
  };

  return {
    upstream: resolve(edges.filter((edge) => edge.to === nodeId).map((edge) => edge.from)),
    downstream: resolve(edges.filter((edge) => edge.from === nodeId).map((edge) => edge.to)),
  };
}
